import React, { useState, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLoginChallenge } from '../hydraApiService';
import classes from './Login.module.css';
import Carousel from './Carousel';

const SLIDES = [
  {
    id: 1,
    image: './login-img1.png',
    title: 'Products With Stories You Can\'t See',
    description: 'Every product has a hidden journey — from materials and manufacturing to usage, ownership, and recycling. Without visibility, brands lose trust, consumers lose clarity, and sustainability loses momentum.',
    brand: 'Provinyx'
  },
  {
    id: 2,
    image: './login-img2.png',
    title: 'The Digital Passport for Every Product',
    description: 'Provinyx assigns a secure Digital Product Passport to each item, storing verified data about origin, materials, certifications, repairs, recycling, and ownership updates. Scan a QR and instantly access proof you can trust, transparent, tamper-proof, and circular by design.',
  },
  {
    id: 3,
    image: './login-img3.png',
    title: 'Transparency That Builds Trust & Circular Value',
    description: 'With Provinyx, brands unlock regulatory compliance, circular reuse, and end-of-life efficiency. Consumers gain confidence, traceability, and environmental clarity. Together, we accelerate sustainability by turning product data into proven impact.'
  }
];

const inputReducer = (state, action) => {
  if (action.type === 'USER_INPUT') {
    return { value: action.val, isValid: action.val.trim().length > 0 };
  }
  if (action.type === 'INPUT_BLUR') {
    return { value: state.value, isValid: state.value.trim().length > 0 };
  }
  return { value: '', isValid: false };
};

const passwordReducer = (state, action) => {
  if (action.type === 'USER_INPUT') {
    return { value: action.val, isValid: action.val.trim().length > 6 };
  }
  if (action.type === 'INPUT_BLUR') {
    return { value: state.value, isValid: state.value.trim().length > 6 };
  }
  return { value: '', isValid: false };
};

const Login = () => {
  const navigate = useNavigate();
  const [formIsValid, setFormIsValid] = useState(false);
  const [notification, setNotification] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const [inputState, dispatchInput] = useReducer(inputReducer, {
    value: '',
    isValid: null,
  });

  const [passwordState, dispatchPassword] = useReducer(passwordReducer, {
    value: '',
    isValid: null,
  });

  useEffect(() => {
    const identifier = setTimeout(() => {
      setFormIsValid(inputState.isValid && passwordState.isValid);
    }, 500);
    return () => clearTimeout(identifier);
  }, [inputState.isValid, passwordState.isValid]);

  const inputChangeHandler = (event) => {
    dispatchInput({ type: 'USER_INPUT', val: event.target.value });
  };

  const passwordChangeHandler = (event) => {
    dispatchPassword({ type: 'USER_INPUT', val: event.target.value });
  };

  const validateInputHandler = () => {
    dispatchInput({ type: 'INPUT_BLUR' });
  };

  const validatePasswordHandler = () => {
    dispatchPassword({ type: 'INPUT_BLUR' });
  };

  const handlePrevSlide = () => {
    if (currentSlide === 0 || isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => prev - 1);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleNextSlide = () => {
    if (currentSlide === SLIDES.length - 1 || isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => prev + 1);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleDotClick = (index) => {
    if (index === currentSlide || isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const submitHandler = async (event) => {
    event.preventDefault();
    console.log('=== SUBMIT HANDLER FIRED ===');
    console.log('inputState:', inputState);
    console.log('passwordState:', passwordState);
    console.log('formIsValid:', formIsValid);

    if (!inputState.isValid || !passwordState.isValid) {
      setNotification({ message: 'Please enter valid credentials', type: 'error' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    try {
      localStorage.setItem('pending_email', inputState.value);
      localStorage.setItem('pending_password', passwordState.value);
      getLoginChallenge();
    } catch (error) {
      setNotification({ message: error.message || 'Login failed.', type: 'error' });
      setTimeout(() => setNotification(null), 4000);
    }
  };

  const notificationStyle = {
    marginTop: '20px',
    padding: '16px',
    borderRadius: '8px',
    border: notification?.type === 'success' ? '1px solid #86efac' : '1px solid #fca5a5',
    backgroundColor: notification?.type === 'success' ? '#dcfce7' : '#fee2e2',
    color: notification?.type === 'success' ? '#166534' : '#991b1b',
    animation: 'slideDown 0.3s ease-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    fontWeight: 'bold',
    cursor: 'pointer',
    opacity: 0.7,
    marginLeft: '15px',
    color: 'inherit',
  };


  useEffect(() => {
  const timer = setInterval(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev === SLIDES.length - 1 ? 0 : prev + 1));
      setTimeout(() => setIsAnimating(false), 600);
    }
  }, 15000);

  return () => clearInterval(timer);
}, [isAnimating]);

  return (
    <div className={classes.loginContainer}>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .${classes.slideImageWrapper} {
          flex: 0 0 auto !important;
          width: 100% !important;
          max-width: 300px !important;
          height: 220px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          margin-bottom: 15px !important;
          position: relative !important;
        }

        .${classes.slideImage} {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
        }

        .${classes.carouselContainer} {
          padding: 15px !important;
        }

        @media (max-width: 1024px) {
          .${classes.loginContainer} { flex-direction: column !important; height: auto !important; min-height: 100vh !important; }
          .${classes.leftSection} { flex: 0 0 auto !important; min-height: 45vh !important; max-height: 50vh !important; padding: 20px 15px !important; }
          .${classes.rightSection} { flex: 0 0 auto !important; min-height: 50vh !important; padding: 25px 20px !important; }
          .${classes.slideImageWrapper} { max-width: 260px !important; height: 200px !important; margin-bottom: 12px !important; }
          .${classes.slideContent} { padding: 12px 25px !important; }
          .${classes.slideTitle} { font-size: 22px !important; margin-bottom: 10px !important; }
          .${classes.slideDescription}, .${classes.slideBrand} { font-size: 13px !important; line-height: 1.5 !important; }
          .${classes.carouselControls} { padding: 12px 25px !important; }
          .${classes.navButton} { font-size: 13px !important; padding: 7px 12px !important; }
          .${classes.logoText} { font-size: 1.6rem !important; }
          .${classes.logoText} img { height: 44px !important; }
          .${classes.formWrapper} { max-width: 450px !important; margin: 0 auto !important; }
        }

        @media (max-width: 900px) and (orientation: landscape) {
          .${classes.leftSection} { min-height: 100vh !important; max-height: none !important; padding: 20px 15px !important; }
          .${classes.rightSection} { min-height: 100vh !important; padding: 25px 20px !important; }
          .${classes.carouselContainer} { justify-content: center !important; }
          .${classes.slideImageWrapper} { max-width: 240px !important; height: 180px !important; }
          .${classes.slideContent} { padding: 10px 20px !important; }
          .${classes.slideTitle} { font-size: 20px !important; }
          .${classes.slideDescription}, .${classes.slideBrand} { font-size: 12px !important; }
          .${classes.carouselControls} { padding: 10px 20px !important; }
        }

        @media (max-width: 768px) {
          .${classes.loginContainer} { flex-direction: column !important; height: auto !important; min-height: 100vh !important; }
          .${classes.leftSection} { min-height: 48vh !important; max-height: 50vh !important; padding: 18px 12px !important; }
          .${classes.rightSection} { min-height: 52vh !important; padding: 20px 15px !important; }
          .${classes.slideImageWrapper} { max-width: 220px !important; height: 170px !important; margin-bottom: 10px !important; }
          .${classes.slideContent} { padding: 10px 18px !important; }
          .${classes.slideTitle} { font-size: 19px !important; margin-bottom: 9px !important; }
          .${classes.slideDescription}, .${classes.slideBrand} { font-size: 12px !important; line-height: 1.45 !important; }
          .${classes.carouselControls} { padding: 10px 18px !important; gap: 8px !important; }
          .${classes.navButton} { font-size: 12px !important; padding: 6px 10px !important; }
          .${classes.navButton} svg { width: 15px !important; height: 15px !important; }
          .${classes.logoText} { font-size: 1.4rem !important; }
          .${classes.logoText} img { height: 38px !important; }
          .${classes.logoSection} { margin-bottom: 25px !important; }
          .${classes.formContainer} { padding: 1.5rem !important; }
          .${classes.buttonRow} { flex-direction: column !important; gap: 0.75rem !important; }
          .${classes.loginBtn}, .${classes.signupBtn} { width: 100% !important; }
        }

        @media (max-width: 480px) {
          .${classes.leftSection} { min-height: 42vh !important; padding: 15px 10px !important; }
          .${classes.rightSection} { min-height: 58vh !important; padding: 18px 10px !important; }
          .${classes.slideImageWrapper} { max-width: 190px !important; height: 145px !important; margin-bottom: 8px !important; }
          .${classes.slideContent} { padding: 8px 15px !important; }
          .${classes.slideTitle} { font-size: 17px !important; margin-bottom: 8px !important; }
          .${classes.slideDescription}, .${classes.slideBrand} { font-size: 11px !important; line-height: 1.4 !important; }
          .${classes.carouselControls} { padding: 8px 15px !important; gap: 6px !important; }
          .${classes.navButton} { font-size: 11px !important; padding: 5px 8px !important; gap: 4px !important; }
          .${classes.navButton} svg { width: 14px !important; height: 14px !important; }
          .${classes.dotsContainer} { gap: 7px !important; }
          .${classes.logoText} { font-size: 1.2rem !important; }
          .${classes.logoText} img { height: 32px !important; }
          .${classes.logoSection} { margin-bottom: 22px !important; }
          .${classes.formContainer} { padding: 1.25rem !important; }
          .${classes.input} { padding: 0.7rem 0.9rem !important; font-size: 0.85rem !important; }
          .${classes.loginBtn}, .${classes.signupBtn} { padding: 0.7rem 0.9rem !important; font-size: 0.85rem !important; }
          .${classes.forgotLink} { font-size: 0.7rem !important; }
        }

        @media (max-width: 360px) {
          .${classes.leftSection} { min-height: 38vh !important; padding: 12px 8px !important; }
          .${classes.rightSection} { min-height: 62vh !important; padding: 15px 8px !important; }
          .${classes.slideImageWrapper} { max-width: 170px !important; height: 130px !important; margin-bottom: 6px !important; }
          .${classes.slideContent} { padding: 6px 12px !important; }
          .${classes.slideTitle} { font-size: 15px !important; margin-bottom: 6px !important; }
          .${classes.slideDescription}, .${classes.slideBrand} { font-size: 10px !important; line-height: 1.35 !important; }
          .${classes.carouselControls} { padding: 6px 12px !important; gap: 5px !important; }
          .${classes.navButton} { font-size: 10px !important; padding: 4px 6px !important; }
          .${classes.navButton} svg { width: 12px !important; height: 12px !important; }
          .${classes.dot} { width: 6px !important; height: 6px !important; }
          .${classes.dotActive} { width: 8px !important; height: 8px !important; }
          .${classes.logoText} { font-size: 1rem !important; }
          .${classes.logoText} img { height: 28px !important; }
          .${classes.logoSection} { margin-bottom: 20px !important; }
          .${classes.formContainer} { padding: 1rem !important; }
          .${classes.input} { padding: 0.65rem 0.8rem !important; font-size: 0.8rem !important; }
          .${classes.loginBtn}, .${classes.signupBtn} { padding: 0.65rem 0.8rem !important; font-size: 0.8rem !important; }
        }
      `}</style>

      {/* Left Section - Carousel */}
      <div className={classes.leftSection}>
        <Carousel initialSlide={0}/>
      </div>

      {/* Right Section */}
      <div className={classes.rightSection}>
        <div className={classes.formWrapper}>

          {/* ── Logo: same as Navbar but larger ── */}
          <div className={classes.logoSection}>
              <img
                src="/Provinyx-logo-LightTheme.png"
                alt="Provinyx"
                className={classes.logoIcon}
  />
</div>

          <div className={classes.formContainer}>
            <form onSubmit={submitHandler} className={classes.form}>
              <input
                type="text"
                placeholder="Email or Username"
                value={inputState.value}
                onChange={inputChangeHandler}
                onBlur={validateInputHandler}
                className={classes.input}
              />
              <input
                type="password"
                placeholder="Password"
                value={passwordState.value}
                onChange={passwordChangeHandler}
                onBlur={validatePasswordHandler}
                className={classes.input}
              />

              <div className={classes.buttonRow}>
                <button
                  type="submit"
                  className={classes.loginBtn}
                  disabled={!formIsValid}
                >
                  Log In
                </button>
                <button
                  type="button"
                  className={classes.signupBtn}
                  onClick={() => navigate('/signup')}
                >
                  Sign Up
                </button>
              </div>

              <div className={classes.forgotRow}>
                <span
                  className={classes.forgotLink}
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot Password?
                </span>
              </div>
            </form>

            {notification && (
              <div style={notificationStyle}>
                <span style={{ fontWeight: 500 }}>{notification.message}</span>
                <button
                  onClick={() => setNotification(null)}
                  style={closeButtonStyle}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;